import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Clock, Package, ShoppingCart, UserPlus, Eye } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import { useLocation } from '@/contexts/LocationContext';

interface Activity {
  id: string;
  type: 'visit' | 'package' | 'sale' | 'customer_added';
  customer: string;
  description: string;
  time: string;
  amount?: string;
}

interface ActivityLogEntry {
  id: string;
  action: string;
  entity: string;
  ts: string;
  detail?: any;
}

const getActivityIcon = (type: Activity['type']) => {
  switch (type) {
    case 'sale': return ShoppingCart;
    case 'visit': return Eye;
    case 'package': return Package;
    case 'customer_added': return UserPlus;
  }
};

const getActivityColor = (type: Activity['type']) => {
  switch (type) {
    case 'sale': return 'bg-success/20 text-success';
    case 'visit': return 'bg-info/20 text-info';
    case 'package': return 'bg-warning/20 text-warning';
    case 'customer_added': return 'bg-accent/20 text-accent';
  }
};

export const RecentActivity = () => {
  const { companyId, loading: authLoading } = useAuth();
  const { currentLocation } = useLocation();

  // Fetch recent activities from multiple sources
  const { data: recentSales = [] } = useQuery({
    queryKey: ['recentSales', companyId, currentLocation?.id],
    queryFn: async () => {
      if (!companyId) return [];
      
      let query = supabase
        .from('sales')
        .select(`
          id,
          total_net,
          sale_datetime,
          customer:customers (
            first_name,
            last_name
          )
        `)
        .eq('company_id', companyId)
        .eq('status', 'completed');

      if (currentLocation?.id) {
        query = query.eq('location_id', currentLocation.id);
      }

      const { data, error } = await query.order('sale_datetime', { ascending: false }).limit(5);
      
      if (error) {
        console.error('Error fetching recent sales:', error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!companyId && !authLoading,
  });

  const { data: recentVisits = [] } = useQuery({
    queryKey: ['recentVisitRecords', companyId, currentLocation?.id],
    queryFn: async () => {
      if (!companyId) return [];
      
      let query = supabase
        .from('calendar_events')
        .select(`
          id,
          start_date,
          start_time,
          customer:customers (
            first_name,
            last_name
          )
        `)
        .eq('company_id', companyId)
        .eq('event_type', 'visit_record')
        .is('deleted_at', null);

      if (currentLocation?.id) {
        query = query.eq('location_id', currentLocation.id);
      }

      const { data, error } = await query
        .order('start_date', { ascending: false })
        .order('start_time', { ascending: false })
        .limit(5);
      
      if (error) {
        console.error('Error fetching recent visit records:', error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!companyId && !authLoading,
  });

  const { data: recentPackages = [] } = useQuery({
    queryKey: ['recentPackages', companyId, currentLocation?.id],
    queryFn: async () => {
      if (!companyId) return [];
      
      let query = supabase
        .from('packages')
        .select(`
          id,
          created_at,
          total_net,
          customer:customers (
            first_name,
            last_name
          )
        `)
        .eq('company_id', companyId);

      if (currentLocation?.id) {
        query = query.eq('location_id', currentLocation.id);
      }

      const { data, error } = await query.order('created_at', { ascending: false }).limit(5);
      
      if (error) {
        console.error('Error fetching recent packages:', error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!companyId && !authLoading,
  });

  const { data: recentCustomers = [] } = useQuery({
    queryKey: ['recentCustomers', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      
      const { data, error } = await supabase
        .from('customers')
        .select('id, first_name, last_name, created_at')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (error) {
        console.error('Error fetching recent customers:', error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!companyId && !authLoading,
  });

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - time.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Şimdi';
    if (diffInMinutes === 1) return '1 dakika önce';
    if (diffInMinutes < 60) return `${diffInMinutes} dakika önce`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours === 1) return '1 saat önce';
    if (diffInHours < 24) return `${diffInHours} saat önce`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return '1 gün önce';
    return `${diffInDays} gün önce`;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getCustomerName = (customer: any) => {
    if (!customer) return 'Bilinmeyen Müşteri';
    return `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Adsız Müşteri';
  };

  // Combine all activities and sort by timestamp
  const allActivities: Activity[] = [
    ...recentSales.map(sale => ({
      id: `sale-${sale.id}`,
      type: 'sale' as const,
      customer: getCustomerName(sale.customer),
      description: 'Satış tamamlandı',
      time: formatTimeAgo(sale.sale_datetime),
      amount: formatCurrency(sale.total_net),
    })),
    ...recentVisits.map(visit => ({
      id: `visit-${visit.id}`,
      type: 'visit' as const,
      customer: getCustomerName(visit.customer),
      description: 'Ziyaret kaydı',
      time: formatTimeAgo(`${visit.start_date}${visit.start_time ? `T${visit.start_time}` : ''}`),
    })),
    ...recentPackages.map(pkg => ({
      id: `package-${pkg.id}`,
      type: 'package' as const,
      customer: getCustomerName(pkg.customer),
      description: 'Yeni paket oluşturuldu',
      time: formatTimeAgo(pkg.created_at),
    })),
    ...recentCustomers.map(customer => ({
      id: `customer-${customer.id}`,
      type: 'customer_added' as const,
      customer: getCustomerName(customer),
      description: 'Yeni müşteri kaydı',
      time: formatTimeAgo(customer.created_at),
    })),
  ].slice(0, 8); // Limit to 8 most recent activities

  if (authLoading) {
    return (
      <div className="glass-panel p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Skeleton className="w-10 h-10 rounded-xl" />
          <div>
            <Skeleton className="w-32 h-6 mb-2" />
            <Skeleton className="w-24 h-4" />
          </div>
        </div>
        <div className="space-y-4">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="flex items-start gap-4 p-4 rounded-xl bg-white/5">
              <Skeleton className="w-10 h-10 rounded-lg" />
              <div className="flex-1">
                <Skeleton className="w-24 h-5 mb-2" />
                <Skeleton className="w-32 h-4 mb-2" />
                <Skeleton className="w-16 h-3" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="glass-panel p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-info/10 flex items-center justify-center">
          <Clock className="w-5 h-5 text-info" />
        </div>
        <div>
          <h2 className="text-h3">Son Aktiviteler</h2>
          <p className="text-caption text-muted-foreground">Güncel işlemler</p>
        </div>
      </div>

      <div className="space-y-4">
        {allActivities.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Henüz aktivite bulunmuyor</p>
          </div>
        ) : (
          allActivities.map((activity) => {
            const Icon = getActivityIcon(activity.type);
            return (
              <div
                key={activity.id}
                className="flex items-start gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/8 transition-all duration-200"
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getActivityColor(activity.type)}`}>
                  <Icon className="w-4 h-4" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold truncate">{activity.customer}</span>
                    {activity.amount && (
                      <Badge variant="secondary" className="font-tabular">
                        {activity.amount}
                      </Badge>
                    )}
                  </div>
                  <p className="text-caption text-muted-foreground">{activity.description}</p>
                  <div className="flex items-center gap-1 mt-2">
                    <Clock className="w-3 h-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{activity.time}</span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};